import { Component, OnInit } from '@angular/core';
import { TrendReportService } from '../services/trend-report.service';
import { ActivatedRoute } from '@angular/router';
import { LocationInformationService } from '../services/location-information.service';
import { FormGroup, FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { TrendService } from '../services/trend.service';
import { LoadingService } from '../services/loading.service';
import { MatFormField, MatSelect, MatOption } from '@angular/material/select';
import { NgFor, NgIf } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { DateSelectionComponent } from '../date-selection/date-selection.component';

import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {GridComponent, TooltipComponent, LegendComponent, TitleComponent} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);

dayjs.extend(utc);
dayjs.extend(timezone);

export interface chartDataSource{
  seriesname: string;
  data: any[];
}

@Component({
    selector: 'app-severity-trend-chart',
    templateUrl: './severity-trend-chart.component.html',
    styleUrls: ['./severity-trend-chart.component.scss'],
    imports: [ReactiveFormsModule, FormsModule, MatFormField, MatSelect, NgFor, MatOption, MatButton, DateSelectionComponent, NgIf, NgxEchartsDirective],
    providers: [provideEchartsCore({ echarts })]
})
export class SeverityTrendChartComponent implements OnInit {
  dataSource: any;
  chartOption: any;
  trendChartData: any;

  shiftDateLabels: any[] = [];
  trendChartDataSource: chartDataSource[] = [];
  plant: string;

  showChart: boolean = false;

  public fromDateString: string;
  public toDateString: string;
  fg_hdr: FormGroup = new FormGroup('');
  manualInput: boolean = false;

  public yMaxVal: number;
  public groupBy: string = 'day';
  currentShift: string = 'True';
  
  lines: { Line: string }[] = [];
  selectedLines: string[] = [];

  seriesColors: Record<string, string> = {
    Major: '#5b6ee1',
    Critical: '#e74c3c',
    Minor: '#f5a623'
  };

  constructor(
    private locationInformationService: LocationInformationService,
    private trendReportService: TrendReportService,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
    private trendService: TrendService,
    private formBuilder: FormBuilder) {
    this.plant = this.route.snapshot.paramMap.get('plant')!;
    this.locationInformationService.changeLocation(true);
    this.locationInformationService.changePlant(this.plant);
    this.locationInformationService.changePlantView(true);
  }

  ngOnInit() {
    setTimeout(() => {
      if (this.fromDateString && this.toDateString) {
        this.getSeverityTrendData();
      }
    });
  
    this.buildEmptyChartOption();
    
    this.trendService.getLines(this.plant).subscribe(res => {
      this.lines = res.Body;
    });
  }
  
  changeLines() {
    this.getSeverityTrendData();
  }

  getSeverityTrendData() {
    const lineSelect = this.selectedLines.length ? this.selectedLines.toString() : null;

    this.loadingService.show();

    this.trendReportService
      .trendDateRange(this.plant, this.fromDateString, this.toDateString, this.groupBy, lineSelect)
      .subscribe({
        next: (response) => {
          this.trendChartData = response.Body;
          this.shiftDateLabels = [];
          this.trendChartDataSource = [];

          if (this.trendChartData.length > 0) {
            this.shiftDateLabels = this.trendChartData
              .map(obj => obj.ShiftDate.substring(5, 10))
              .filter((elem, index, self) => index === self.indexOf(elem));

            this.trendChartDataSource.push({
              seriesname: 'Major',
              data: this.trendChartData.map(obj => ({
                label: obj.ShiftDate.substring(5, 10),
                value: obj.Major
              }))
            });

            this.trendChartDataSource.push({
              seriesname: 'Critical',
              data: this.trendChartData.map(obj => ({
                label: obj.ShiftDate.substring(5, 10),
                value: obj.Critical
              }))
            });

            this.trendChartDataSource.push({
              seriesname: 'Minor',
              data: this.trendChartData.map(obj => ({
                label: obj.ShiftDate.substring(5, 10),
                value: obj.Minor
              }))
            });

            this.yMaxVal =
              Math.max.apply(
                Math,
                this.trendChartData.map(o => Math.max(o.Minor, o.Major, o.Critical))
              ) + 1;

            this.buildChartOption();
            this.showChart = true;
          } else {
            this.showChart = false;
            this.shiftDateLabels = [];
            this.yMaxVal = 1;
            this.trendChartDataSource = [{ seriesname: 'Major', data: [] }];
            this.buildEmptyChartOption();
          }
        },
        error: () => {
          this.showChart = false;
        },
        complete: () => {
          this.loadingService.hide();
        }
      });
  }

  buildChartOption() {
    const shouldRotateDates = this.shiftDateLabels.length >= 30;

    this.chartOption = {
      title: {
        text: `${this.plant} Severity Chart`,
        left: 'center',
        top: 10,
        textStyle: {fontSize: 14, fontWeight: '600', color: '#666'
        }
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {color: '#444' },
        formatter: (params: any[]) => this.getTooltipFormatter(params)
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'middle',
        itemWidth: 14,
        itemHeight: 14,
        textStyle: { color: '#666', fontSize: 12},
        data: ['Major', 'Critical', 'Minor']
      },
      grid: {left: 85,right: 90,top: 60, bottom: 60, containLabel: false },
      xAxis: {
        type: 'category',
        name: 'Date',
        nameLocation: 'middle',
        nameGap: 35,
        data: this.shiftDateLabels,
        boundaryGap: false,
        nameTextStyle: { color: '#666',fontSize: 14},
        axisLine: {lineStyle: { color: '#ccc'} },
        axisTick: {show: false},
        axisLabel: {
          rotate: shouldRotateDates ? 45 : 0,
          interval: 0,
          hideOverlap: true,
          color: '#666',
          fontSize: 11
        },
        splitLine: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        name: 'EOLA Finding By Catergory',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 55,
        min: 0,
        max: this.yMaxVal,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 12,
          formatter: (value: number) => `${value}`
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e9edf5',
            type: 'solid'
          }
        }
      },
      series: this.trendChartDataSource.map(series =>
        this.createSeries(series.seriesname, series.data)
      )
    };
  }

  buildEmptyChartOption() {
    this.chartOption = {
      title: {
        text: `${this.plant} Severity Chart`,
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 14,
          fontWeight: '600',
          color: '#666'
        }
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'middle',
        data: ['Major', 'Critical', 'Minor']
      },
      grid: {
        left: 85,
        right: 90,
        top: 60,
        bottom: 60
      },
      xAxis: {
        type: 'category',
        name: 'Date',
        nameLocation: 'middle',
        nameGap: 35,
        data: []
      },
      yAxis: {
        type: 'value',
        name: 'EOLA Finding By Catergory',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 55,
        min: 0,
        max: 1
      },
      series: []
    };
  }

  createSeries(name: string, dataArr: any[]) {
    const color = this.seriesColors[name] || '#999';
    const dataMap = new Map(dataArr.map(x => [x.label, x.value]));

    return {
      name,
      type: 'line',
      smooth: false,
      symbol: 'circle',
      symbolSize: 6,
      showSymbol: true,
      lineStyle: {
        width: 1,
        color
      },
      itemStyle: {
        color: '#ffffff',
        borderColor: color,
        borderWidth: 1
      },
      data: this.shiftDateLabels.map(d => dataMap.get(d) ?? null)
    };
  }

  getTooltipFormatter(params: any[]): string {
    if (!params || !params.length) {
      return '';
    }

    const dateLabel = params[0].axisValueLabel || params[0].axisValue;

    const validParams = params.filter((p: any) =>
      p.data !== null &&
      p.data !== undefined &&
      p.data !== ''
    );

    if (!validParams.length) {
      return `<div>${dateLabel}</div>`;
    }

    const rows = validParams.map((p: any) => {
      const color = this.seriesColors[p.seriesName] || '#999';
      const value = Number(p.data).toLocaleString();

      const marker = `
        <span style="
          display:inline-block;
          width:10px;
          height:10px;
          margin-right:6px;
          border:2px solid ${color};
          border-radius:50%;
          background:#fff;
          vertical-align:middle;
          box-sizing:border-box;
        "></span>
      `;

      return `
        <div style="display:flex; justify-content:space-between; gap:16px; align-items:center;">
          <span>${marker}${p.seriesName}</span>
          <span><b>${value}</b></span>
        </div>
      `;
    }).join('');

    return `
      <div style="min-width:170px;">
        <div style="margin-bottom:6px;">${dateLabel}</div>
        ${rows}
      </div>
    `;
  }

  onDateChange(e: any) {
    this.fromDateString = e.fromDate;
    this.toDateString = e.toDate;
    this.currentShift = e.currentShift;

    this.getSeverityTrendData();
  }
}
